import React from 'react';
import { Typography, Box } from '@mui/material';

interface MessageFormatterProps {
  message?: string;
  content?: string;
  searchResults?: any[];
  isPublicView?: boolean;
}

const MessageFormatter: React.FC<MessageFormatterProps> = ({ 
  message, 
  content, 
  searchResults, 
  isPublicView = false 
}) => {
  const textContent = message || content || '';
  
  // Function to parse inline formatting within text
  const parseInlineFormatting = (text: string): React.ReactNode[] => {
    const elements: React.ReactNode[] = [];
    let currentIndex = 0;
    
    // Find all **text** patterns
    const boldPattern = /\*\*(.*?)\*\*/g;
    let match;
    
    while ((match = boldPattern.exec(text)) !== null) {
      // Add text before the bold part
      if (match.index > currentIndex) {
        elements.push(text.slice(currentIndex, match.index));
      }
      
      // Add the bold part
      elements.push(
        <strong key={match.index} style={{ fontWeight: 'bold' }}>
          {match[1]}
        </strong>
      );
      
      currentIndex = match.index + match[0].length;
    }
    
    // Add remaining text after the last match
    if (currentIndex < text.length) {
      elements.push(text.slice(currentIndex));
    }
    
    // If no matches found, return the original text
    return elements.length > 0 ? elements : [text];
  };

  // Simple function to convert basic markdown-like formatting to React elements
  const formatMessage = (text: string) => {
    const lines = text.split('\n');
    const formattedElements: React.ReactNode[] = [];
    
    lines.forEach((line, index) => {
      // Skip empty lines at the beginning, but preserve spacing between content
      if (line.trim() === '') {
        if (formattedElements.length > 0) {
          formattedElements.push(<Box key={`space-${index}`} sx={{ height: '8px' }} />);
        }
        return;
      }
      
      // Check for diagnosis header that might not have proper markdown
      if (line.includes('Diagnosis Found!') && line.includes('Similarity:')) {
        // Special handling for diagnosis header
        formattedElements.push(
          <Box 
            key={index}
            sx={{
              bgcolor: '#e8f5e8',
              border: '2px solid #4caf50',
              borderRadius: 2,
              p: 2,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box sx={{ fontSize: '24px' }}>🎯</Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#2e7d32',
                flexGrow: 1
              }}
            >
              {line.replace('🎯 ', '')}
            </Typography>
          </Box>
        );
      }
      // Handle different formatting patterns
      else if (line.startsWith('🎯 **') && line.includes('**')) {
        // Main title with emoji - Diagnosis Found
        const title = line.replace(/🎯 \*\*(.*?)\*\*/, '$1');
        formattedElements.push(
          <Box 
            key={index}
            sx={{
              bgcolor: '#e8f5e8',
              border: '2px solid #4caf50',
              borderRadius: 2,
              p: 2,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box sx={{ fontSize: '24px' }}>🎯</Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#2e7d32',
                flexGrow: 1
              }}
            >
              {title}
            </Typography>
          </Box>
        );
      }
      else if (line.startsWith('**') && line.endsWith('**')) {
        // Bold headings
        const heading = line.replace(/\*\*(.*?)\*\*/, '$1');
        let color = '#1A1A1A';
        let emoji = '';
        let bgcolor = 'transparent';
        let borderColor = 'transparent';
        
        // Extract emoji and adjust styling based on content
        if (heading.startsWith('🔬')) {
          color = '#2e7d32'; // Green for diagnosis
          emoji = '🔬';
          bgcolor = '#f1f8e9';
          borderColor = '#8bc34a';
        } else if (heading.startsWith('🚨')) {
          color = '#d32f2f'; // Red for emergency
          emoji = '🚨';
          bgcolor = '#ffebee';
          borderColor = '#f44336';
        } else if (heading.startsWith('⚠️')) {
          color = '#f57c00'; // Orange for urgent
          emoji = '⚠️';
          bgcolor = '#fff3e0';
          borderColor = '#ff9800';
        } else if (heading.startsWith('🔴')) {
          color = '#d32f2f'; // Red for high priority
          emoji = '🔴';
          bgcolor = '#ffebee';
          borderColor = '#f44336';
        } else if (heading.startsWith('🟠')) {
          color = '#f57c00'; // Orange for urgent gp
          emoji = '🟠';
          bgcolor = '#fff3e0';
          borderColor = '#ff9800';
        } else if (heading.startsWith('🟡')) {
          color = '#f57c00'; // Orange for medium priority
          emoji = '🟡';
          bgcolor = '#fffde7';
          borderColor = '#ffeb3b';
        } else if (heading.startsWith('🟢')) {
          color = '#388e3c'; // Green for low priority
          emoji = '🟢';
          bgcolor = '#f1f8e9';
          borderColor = '#4caf50';
        } else if (heading.startsWith('📋')) {
          color = '#1976d2'; // Blue for recommendations
          emoji = '📋';
          bgcolor = '#e3f2fd';
          borderColor = '#2196f3';
        } else if (heading.startsWith('🔍')) {
          color = '#6a1b9a'; // Purple for symptoms
          emoji = '🔍';
          bgcolor = '#f3e5f5';
          borderColor = '#9c27b0';
        } else if (heading.startsWith('🏥')) {
          color = '#1976d2'; // Blue for next steps
          emoji = '🏥';
          bgcolor = '#e3f2fd';
          borderColor = '#2196f3';
        }
        
        const cleanHeading = heading.replace(/^[🔬🚨⚠️🔴🟠🟡🟢📋🔍🏥]\s*/, '');
        
        // Special styling for priority levels
        const isPriorityLevel = heading.includes('Priority Level');
        
        formattedElements.push(
          <Box
            key={index}
            sx={{
              mt: 1.5,
              mb: 1,
              p: isPriorityLevel ? 1.5 : 1,
              bgcolor: bgcolor,
              border: bgcolor !== 'transparent' ? `1px solid ${borderColor}` : 'none',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {emoji && <Box sx={{ fontSize: '18px' }}>{emoji}</Box>}
            <Typography 
              variant={isPriorityLevel ? "h6" : "body1"} 
              sx={{ 
                fontWeight: 'bold', 
                color: color,
                flexGrow: 1
              }}
            >
              {cleanHeading}
            </Typography>
          </Box>
        );
      }
      else if (line.startsWith('• ')) {
        // Bullet points - handle inline formatting
        const bulletText = line.replace('• ', '');
        const formattedBulletText = parseInlineFormatting(bulletText);
        
        formattedElements.push(
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              ml: 2,
              mb: 0.8,
              p: 0.5,
              borderRadius: 1,
              '&:hover': {
                bgcolor: '#f5f5f5'
              }
            }}
          >
            <Box sx={{ 
              color: '#1976d2', 
              fontSize: '14px', 
              mt: 0.2,
              fontWeight: 'bold' 
            }}>
              •
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#444',
                lineHeight: 1.4
              }}
            >
              {formattedBulletText}
            </Typography>
          </Box>
        );
      }
      else if (line.startsWith('💡 *') && line.endsWith('*')) {
        // Italicized notes/disclaimers
        const noteText = line.replace(/💡 \*(.*?)\*/, '$1');
        formattedElements.push(
          <Box
            key={index}
            sx={{
              mt: 2,
              p: 2,
              bgcolor: '#fff9c4',
              borderRadius: 2,
              border: '1px solid #f9a825',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <Box sx={{ fontSize: '20px', mt: 0.2 }}>💡</Box>
            <Typography 
              variant="body2" 
              sx={{ 
                fontStyle: 'italic', 
                color: '#6d4c41',
                lineHeight: 1.5,
                fontWeight: 500
              }}
            >
              {noteText}
            </Typography>
          </Box>
        );
      }
      else {
        // Regular text - handle inline formatting
        const formattedText = parseInlineFormatting(line);
        formattedElements.push(
          <Typography 
            key={index} 
            variant="body2" 
            sx={{ mb: 0.5, color: '#1A1A1A' }}
          >
            {formattedText}
          </Typography>
        );
      }
    });
    
    return formattedElements;
  };

  return (
    <Box>
      {formatMessage(textContent)}
      
      {/* Display Search Results / Pinecone Cases */}
      {searchResults && searchResults.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: '#1976d2' }}>
            📊 Matching Cases from Database ({searchResults.length})
          </Typography>
          {searchResults.map((result: any, index: number) => (
            <Box
              key={index}
              sx={{
                mb: 2,
                p: 2,
                bgcolor: '#f5f5f5',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  {result.case_number || `Case ${index + 1}`}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Similarity: {((result.score || 0) * 100).toFixed(1)}%
                </Typography>
              </Box>
              
              {result.diagnosis && result.diagnosis.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                    🔬 Diagnosis:
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 2, color: '#444' }}>
                    {Array.isArray(result.diagnosis) ? result.diagnosis.join(', ') : result.diagnosis}
                  </Typography>
                </Box>
              )}
              
              {result.symptoms && result.symptoms.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                    😷 Symptoms:
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 2, color: '#444' }}>
                    {Array.isArray(result.symptoms) ? result.symptoms.join(', ') : result.symptoms}
                  </Typography>
                </Box>
              )}
              
              {result.history && result.history.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                    📋 History:
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 2, color: '#444' }}>
                    {Array.isArray(result.history) ? result.history.join(', ') : result.history}
                  </Typography>
                </Box>
              )}
              
              {result.priority && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                    🚨 Priority:
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ ml: 1, color: '#d32f2f', fontWeight: 'bold' }}>
                    {result.priority}
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MessageFormatter; 